import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAllServiceProviders, searchServiceProviders } from "../services/categoriesService";
import { toggleFavorite } from "../services/favoritesService";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";

export const useServicesData = (providerType = null, pageSize = 20) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [searchQuery, setSearchQuery] = useState("");
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [firstLoadComplete, setFirstLoadComplete] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalFreelancers, setTotalFreelancers] = useState(0);
    const [totalBusinesses, setTotalBusinesses] = useState(0);

    const { accessToken, role } = useAuth();

    const selectedCategory = useMemo(() => {
        return new URLSearchParams(location.search).get("category");
    }, [location.search]);

    // Sync URL → state
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const typeFromUrl = params.get("type") || "all";
        const searchFromUrl = params.get("search") || "";
        if (searchFromUrl !== searchQuery) setSearchQuery(searchFromUrl);
    }, [location.search]);

    // Fetch providers
    useEffect(() => {
        let isCurrent = true;
        const fetchProviders = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams(location.search);
                const page = parseInt(params.get("page") || "1");

                const apiParams = { page, page_size: pageSize };
                if (selectedCategory) apiParams.category = selectedCategory;
                if (providerType) {
                    apiParams.type = providerType;
                }

                let data;
                if (searchQuery) data = await searchServiceProviders(searchQuery, apiParams);
                else data = await getAllServiceProviders(apiParams);

                if (!isCurrent) return;

                setProviders(data.results || []);
                setTotalPages(data.total_pages || 1);
                setTotalFreelancers(data.total_freelancers || 0);
                setTotalBusinesses(data.total_businesses || 0);

                setFirstLoadComplete(true); // ✅ mark first load done
            } catch (err) {
                if (isCurrent) setProviders([]);
                console.error("Error fetching providers:", err);
            } finally {
                if (isCurrent) setLoading(false);
            }
        };

        fetchProviders();
        return () => { isCurrent = false; };
    }, [providerType, selectedCategory, searchQuery, location.search]);


    // Handlers to update URL
    const setTypeFilter = (type) => {
        const params = new URLSearchParams(location.search);
        params.set("page", 1);
        if (type === "all") params.delete("type");
        else params.set("type", type);
        navigate({ pathname: location.pathname, search: params.toString() });
    };

    const setSearch = (query) => {
        const params = new URLSearchParams(location.search);
        params.set("search", query);
        params.set("page", 1);
        navigate({ pathname: location.pathname, search: params.toString() });
    };

    const goToPage = (page) => {
        const params = new URLSearchParams(location.search);
        params.set("page", page);
        navigate({ pathname: location.pathname, search: params.toString() });
    };

    // Favorite toggle
    const handleToggleFavorite = async (e, providerId, type) => {
        e.stopPropagation();

        if (!accessToken || !role) {
            toast.info("Login or Sign Up to like");
            return;
        }

        try {
            const response = await toggleFavorite(providerId, type, "POST");
            const { is_favorited } = response;

            setProviders(prev =>
                prev.map(p => {
                    if (p.id !== providerId) return p;
                    const currentCount = p.favorited_count ?? 0;
                    const newCount = is_favorited ? currentCount + 1 : Math.max(0, currentCount - 1);
                    return { ...p, is_favorited, favorited_count: newCount };
                })
            );
        } catch (err) {
            console.error("Error toggling favorite:", err);
        }
    };

    return {
        providerType,
        selectedCategory,
        searchQuery,
        providers,
        loading,
        firstLoadComplete,
        totalPages,
        setTypeFilter,
        setSearch,
        goToPage,
        handleToggleFavorite
    };
};
